import React, { useState, useEffect, useRef } from "react";
import {
  Loader2,
  GraduationCap,
  BookOpen,
  Award,
  CheckCircle2,
  Circle,
  FileDown,
  Presentation,
  Image as ImageIcon,
  Chrome,
} from "lucide-react";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { useActiveProject } from "@/lib/ActiveProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import {
  exportCoursePDF,
  exportCoursePPT,
  exportCoursePNG,
} from "@/lib/exports";

const COURSE_BG =
  "https://images.unsplash.com/photo-1758846946209-52fd824f8a67?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

export default function Training() {
  const { project } = useActiveProject();
  const [topic, setTopic] = useState(
    "FIDIC Yellow Book — Claims & EOT for EPC Contractors"
  );
  const [audience, setAudience] = useState(
    "EPC project managers and contracts engineers"
  );
  const [hours, setHours] = useState(3);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [revealQuiz, setRevealQuiz] = useState({});
  const [exporting, setExporting] = useState(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    if (!project?.custom) return;
    const book = (project.contract_type || "").replace(/^FIDIC\s+/i, "");
    setTopic(`${book || "FIDIC"} — Claims & EOT for the ${project.name} team`);
    if (project.client) {
      setAudience(`${project.client} contracts and project management teams`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const run = async () => {
    setLoading(true);
    setData(null);
    setRevealQuiz({});
    try {
      const { data } = await api.post("/training/generate", {
        topic,
        audience,
        duration_hours: Number(hours) || 3,
      });
      setData(data);
      toast.success("Course generated");
    } catch (e) {
      toast.error("Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const doExport = async (kind) => {
    if (!data) return;
    setExporting(kind);
    try {
      if (kind === "pdf") exportCoursePDF(data);
      else if (kind === "ppt") exportCoursePPT(data);
      else if (kind === "gslide") exportCoursePPT(data, { googleSlides: true });
      else if (kind === "png") await exportCoursePNG(resultsRef.current, data);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`Export failed: ${e?.message || "unknown"}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="training-page">
      <SectionHeader
        overline="Module 05"
        title="Training AI"
        description="Generate industry-grade EPC training modules — objectives, modules, case study, practical exercise and knowledge check per module."
        testid="training-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Course Builder */}
        <div className="lg:col-span-4 bg-slate-900/60 border border-slate-800 rounded-sm p-4 space-y-4 h-fit">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">
            COURSE BUILDER
          </div>
          <div>
            <Label className="text-xs text-slate-400">Topic</Label>
            <Input
              data-testid="training-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Audience</Label>
            <Input
              data-testid="training-audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-slate-400">Duration</Label>
              <span
                className="text-sm font-mono text-cyan-300"
                data-testid="training-hours-value"
              >
                {hours} {hours === 1 ? "hour" : "hours"}
              </span>
            </div>
            <Slider
              data-testid="training-hours"
              min={1}
              max={6}
              step={1}
              value={[hours]}
              onValueChange={(v) => setHours(v[0])}
              className="[&_[role=slider]]:bg-cyan-500 [&_[role=slider]]:border-cyan-500"
            />
            <div className="flex justify-between mt-1 text-[10px] font-mono text-slate-600">
              <span>1h</span>
              <span>6h</span>
            </div>
          </div>
          <Button
            onClick={run}
            disabled={loading}
            data-testid="training-generate-btn"
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Designing course…
              </>
            ) : (
              "Generate Course"
            )}
          </Button>

          {data && (
            <div className="pt-3 border-t border-slate-800 space-y-2" data-testid="training-exports">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-2">
                Download
              </div>
              <ExportBtn testid="export-pdf-btn" onClick={() => doExport("pdf")} icon={FileDown} label="PDF" busy={exporting === "pdf"} />
              <ExportBtn testid="export-ppt-btn" onClick={() => doExport("ppt")} icon={Presentation} label="PowerPoint (.pptx)" busy={exporting === "ppt"} />
              <ExportBtn testid="export-png-btn" onClick={() => doExport("png")} icon={ImageIcon} label="PNG snapshot" busy={exporting === "png"} />
              <ExportBtn testid="export-gslide-btn" onClick={() => doExport("gslide")} icon={Chrome} label="Google Slides" busy={exporting === "gslide"} />
            </div>
          )}
        </div>

        {/* Results */}
        <div className="lg:col-span-8 space-y-4" ref={resultsRef}>
          {!data && !loading && (
            <CertPreview title="Your course will appear here" topic={topic} />
          )}
          {loading && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-10 text-center">
              <Loader2 className="h-8 w-8 text-cyan-400 animate-spin mx-auto mb-3" />
              <div className="font-mono text-xs text-cyan-400 uppercase tracking-[0.2em]">
                Designing modules…
              </div>
            </div>
          )}
          {data && (
            <div className="space-y-4" data-testid="training-results">
              <CertPreview
                title={data.course_title}
                topic={topic}
                cert={data.certification}
              />

              <Panel title="Learning Objectives" icon={BookOpen}>
                <ul className="space-y-1.5 text-sm text-slate-200">
                  {(data.learning_objectives || []).map((o, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-cyan-400 font-mono text-xs mt-0.5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {o}
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel title="Modules" icon={GraduationCap}>
                <Accordion
                  type="single"
                  collapsible
                  defaultValue="m-0"
                  className="w-full"
                >
                  {(data.modules || []).map((m, i) => (
                    <AccordionItem
                      key={i}
                      value={`m-${i}`}
                      className="border-slate-800"
                    >
                      <AccordionTrigger
                        className="text-slate-100 hover:no-underline hover:text-cyan-400"
                        data-testid={`module-${i}`}
                      >
                        <div className="flex items-center justify-between w-full pr-4">
                          <span className="text-sm">{m.name}</span>
                          <span className="text-xs font-mono text-slate-500">
                            {m.duration_minutes} min
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4">
                        <ModuleBlock
                          title="Topics"
                          items={(m.topics || []).map((t) => `▸ ${t}`)}
                        />
                        {m.case_study && (
                          <ModuleBlock title="Case Study" body={m.case_study} />
                        )}
                        {m.practical_exercise && (
                          <ModuleBlock
                            title="Practical Exercise"
                            body={m.practical_exercise}
                          />
                        )}
                        {m.knowledge_check?.q && (
                          <KnowledgeCheck
                            modIndex={i}
                            q={m.knowledge_check}
                            reveal={revealQuiz[i]}
                            onReveal={() =>
                              setRevealQuiz({ ...revealQuiz, [i]: true })
                            }
                          />
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Panel>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExportBtn({ icon: Icon, label, onClick, busy, testid }) {
  return (
    <Button
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      variant="outline"
      className="w-full justify-start bg-transparent border-slate-700 text-slate-100 hover:bg-slate-800 hover:text-cyan-300 rounded-sm"
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
      ) : (
        <Icon className="h-3.5 w-3.5 mr-2 text-cyan-400" />
      )}
      {label}
    </Button>
  );
}

function ModuleBlock({ title, body, items }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-1.5">
        {title}
      </div>
      {items ? (
        <ul className="space-y-1 text-sm pl-1">
          {items.map((t, j) => (
            <li key={j} className="flex gap-1.5 text-slate-200">
              <span>{t}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-line">
          {body}
        </p>
      )}
    </div>
  );
}

function KnowledgeCheck({ modIndex, q, reveal, onReveal }) {
  return (
    <div data-testid={`knowledge-check-${modIndex}`}>
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-1.5 flex items-center gap-2">
        Knowledge Check
        <Badge className="rounded-sm bg-slate-800 border border-slate-700 text-slate-300 h-4 px-1.5 text-[9px]">
          1 question
        </Badge>
      </div>
      <div
        className="border border-slate-800 bg-slate-950/60 rounded-sm p-3"
        data-testid={`quiz-q-${modIndex}`}
      >
        <div className="text-sm text-slate-100 mb-2 flex gap-2">
          <span className="font-mono text-cyan-400 text-xs">Q</span>
          {q.q}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {(q.options || []).map((o, j) => {
            const correct = reveal && j === q.answer_index;
            return (
              <button
                key={j}
                onClick={onReveal}
                data-testid={`quiz-q-${modIndex}-opt-${j}`}
                className={`text-left text-xs px-2.5 py-1.5 rounded-sm border flex items-center gap-2 transition-colors ${
                  correct
                    ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                    : "border-slate-800 bg-slate-900 hover:border-slate-700 text-slate-300"
                }`}
              >
                {correct ? (
                  <CheckCircle2 className="h-3 w-3" />
                ) : (
                  <Circle className="h-3 w-3 text-slate-600" />
                )}
                {String.fromCharCode(65 + j)}. {o}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CertPreview({ title, topic, cert }) {
  return (
    <div
      className="relative overflow-hidden border border-slate-800 rounded-sm"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(2,6,23,0.85) 0%, rgba(2,6,23,0.95) 100%), url(${COURSE_BG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid="cert-preview"
    >
      <div className="absolute top-0 left-0 right-0 h-1 scanline opacity-70" />
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-cyan-400" />
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">
              Certification Preview
            </span>
          </div>
          {cert ? (
            <Badge className="bg-cyan-500/15 text-cyan-300 border-cyan-700 rounded-sm border">
              {cert.level} · {cert.credits} CPD
            </Badge>
          ) : null}
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mt-3 leading-tight">
          {title}
        </h2>
        {!cert ? (
          <p className="text-sm text-slate-400 mt-2 max-w-xl">
            Build a complete certified training programme on{" "}
            <span className="text-cyan-300">{topic}</span> — 4 modules with
            case study + practical exercise + knowledge check each, plus a
            shareable certificate.
          </p>
        ) : (
          <p className="text-sm text-slate-300 mt-2 max-w-xl">
            Issued upon completion:{" "}
            <span className="text-cyan-300">{cert.name}</span>
          </p>
        )}
      </div>
    </div>
  );
}

function Panel({ children, title, icon: Icon }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        {Icon ? <Icon className="h-4 w-4 text-cyan-400" /> : null}
        <span className="font-display text-base font-semibold text-slate-50">
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}
